/* eslint-disable max-lines */
import { ColumnType } from 'antd/lib/table';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { BasePage, PageType } from 'src/components/BasePage';
import { DataView } from 'src/components/DataView/DataView';
import { LoanApplicationStatusView } from 'src/components/LoanApplicationStatusView/LoanApplicationStatusView';
import { ResourceNameView } from 'src/components/ResourceNameView/ResourceNameView';
import { Table } from 'src/components/Table/Table';
import { TransactionDto } from 'src/generated-api-client';
import { useLoadResources } from 'src/hooks/useLoadResources';
import { TransactionsHeaderPanel } from 'src/pages/TransactionsPage/components/TransactionsHeaderPanel';
import { InstallmentOptionResourceStore } from 'src/stores/Resources/InstallmentOptionResource';
import { PaymentFreequencyResourceStore } from 'src/stores/Resources/PaymentFreequencyResourceStore';
import { TransactionsStore } from 'src/stores/TransactionsStore/TransactionsStore';
import { FormHelper } from 'src/utils/FormHelper';
import { getPagination } from 'src/utils/PaginationUtils';
import styled from 'styled-components';

export const TransactionsPage = observer(() => {
    const { t } = useTranslation();

    const { isLoadingResources } = useLoadResources([
        InstallmentOptionResourceStore,
        PaymentFreequencyResourceStore,
    ]);

    useEffect(() => {
        TransactionsStore.loadList();

        return () => {
            TransactionsStore.listLoader.reset();
        };
    }, []);

    return (
        <BasePage
            title={t('App.PagesTransactions.Title')}
            pageName="transactions"
            header={<TransactionsHeaderPanel />}
            isLoading={isLoadingResources}
            type={PageType.FULL}
            showOnlyLoading
        >
            <StyledTable
                loading={TransactionsStore.listLoader.isLoading}
                columns={columns}
                dataSource={TransactionsStore.list}
                pagination={getPagination(TransactionsStore)}
            />
        </BasePage>
    );
});

const formNames = FormHelper.createNameProvider<TransactionDto>();

const columns: ColumnType<TransactionDto>[] = [
    {
        key: 'loanApplicationNumber',
        dataIndex: formNames.getPath('loanApplicationNumber'),
        title: (
            <Trans i18nKey="App.Transactions.Columns.LoanApplicationNumber" />
        ),
        render: (value: string) => {
            return <DataView value={value} />;
        },
    },
    {
        key: 'externalMerchantId',
        dataIndex: formNames.getPath('externalMerchantId'),
        title: <Trans i18nKey="App.Transactions.Columns.ExternalMerchantId" />,
        render: (value: string) => {
            return <DataView value={value} />;
        },
    },
    {
        key: 'merchantName',
        dataIndex: formNames.getPath('merchantName'),
        title: <Trans i18nKey="App.Transactions.Columns.MerchantName" />,
        render: (value: string) => {
            return <DataView value={value} />;
        },
    },
    {
        key: 'partnerNumber',
        dataIndex: formNames.getPath('partnerNumber'),
        title: <Trans i18nKey="App.Transactions.Columns.PartnerNumber" />,
        render: (value: string) => {
            return <DataView value={value} />;
        },
    },
    {
        key: 'partnerErpCode',
        dataIndex: formNames.getPath('partnerErpCode'),
        title: <Trans i18nKey="App.Transactions.Columns.PartnerErpCode" />,
        render: (value: string) => {
            return <DataView value={value} />;
        },
    },
    {
        key: 'partnerName',
        dataIndex: formNames.getPath('partnerName'),
        title: <Trans i18nKey="App.Transactions.Columns.PartnerName" />,
        render: (value: string) => {
            return <DataView value={value} />;
        },
    },
    {
        key: 'productName',
        dataIndex: formNames.getPath('productName'),
        title: <Trans i18nKey="App.Transactions.Columns.ProductName" />,
        render: (value: string) => {
            return <DataView value={value} />;
        },
    },
    {
        key: 'iban',
        dataIndex: formNames.getPath('iban'),
        title: <Trans i18nKey="App.Transactions.Columns.IBAN" />,
        render: (value: string) => {
            return <DataView value={value} />;
        },
    },
    {
        key: 'agreementNumber',
        dataIndex: formNames.getPath('agreementNumber'),
        title: <Trans i18nKey="App.Transactions.Columns.AgreementNumber" />,
        render: (value: string) => {
            return <DataView value={value} />;
        },
    },
    {
        key: 'totalRepayableAmount',
        dataIndex: formNames.getPath('totalRepayableAmount'),
        title: (
            <Trans i18nKey="App.Transactions.Columns.TotalRepayableAmount" />
        ),
        render: (value: string) => {
            return <DataView value={value} formatValue="currency-sar" />;
        },
    },
    {
        key: 'installmentOption',
        dataIndex: formNames.getPath('installmentOption'),
        title: <Trans i18nKey="App.Transactions.Columns.InstallmentOption" />,
        render: (value: string) => {
            return (
                <ResourceNameView
                    resourceStore={InstallmentOptionResourceStore}
                    value={value}
                />
            );
        },
    },
    {
        key: 'deductionRatePct',
        dataIndex: formNames.getPath('deductionRatePct'),
        title: <Trans i18nKey="App.Transactions.Columns.DeductionRatePct" />,
        render: (value: string) => {
            return <DataView value={value} formatValue="percent" />;
        },
    },
    {
        key: 'minInstallmentAmount',
        dataIndex: formNames.getPath('minInstallmentAmount'),
        title: (
            <Trans i18nKey="App.Transactions.Columns.MinInstallmentAmount" />
        ),
        render: (value: string) => {
            return <DataView value={value} formatValue="currency-sar" />;
        },
    },
    {
        key: 'maxInstallmentAmount',
        dataIndex: formNames.getPath('maxInstallmentAmount'),
        title: (
            <Trans i18nKey="App.Transactions.Columns.MaxInstallmentAmount" />
        ),
        render: (value: string) => {
            return <DataView value={value} formatValue="currency-sar" />;
        },
    },
    {
        key: 'paymentFrequency',
        dataIndex: formNames.getPath('paymentFrequency'),
        title: <Trans i18nKey="App.Transactions.Columns.PaymentFrequency" />,
        render: (value: string) => {
            return (
                <ResourceNameView
                    resourceStore={PaymentFreequencyResourceStore}
                    value={value}
                />
            );
        },
    },
    {
        key: 'firstPaymentDate',
        dataIndex: formNames.getPath('firstPaymentDate'),
        title: <Trans i18nKey="App.Transactions.Columns.FirstPaymentDate" />,
        render: (value: string) => {
            return <DataView value={value} formatValue="dateTime" />;
        },
    },
    {
        key: 'finalSettlementDate',
        dataIndex: formNames.getPath('finalSettlementDate'),
        title: <Trans i18nKey="App.Transactions.Columns.FinalSettlementDate" />,
        render: (value: string) => {
            return <DataView value={value} formatValue="dateTime" />;
        },
    },
    {
        key: 'term',
        dataIndex: formNames.getPath('term'),
        title: <Trans i18nKey="App.Transactions.Columns.Term" />,
        render: (value: string) => {
            return <DataView value={value} />;
        },
    },
    {
        key: 'collectionCommissionPct',
        dataIndex: formNames.getPath('collectionCommissionPct'),
        title: (
            <Trans i18nKey="App.Transactions.Columns.CollectionCommissionPct" />
        ),
        render: (value: string) => {
            return <DataView value={value} formatValue="percent" />;
        },
    },
    {
        key: 'createdAt',
        dataIndex: formNames.getPath('createdAt'),
        title: <Trans i18nKey="App.Transactions.Columns.CreatedAt" />,
        render: (value: string) => {
            return <DataView value={value} formatValue="dateTime" />;
        },
    },
    {
        key: 'date',
        dataIndex: formNames.getPath('date'),
        title: <Trans i18nKey="App.Transactions.Columns.Date" />,
        render: (value: string) => {
            return <DataView value={value} formatValue="dateTime" />;
        },
    },
    {
        key: 'number',
        dataIndex: formNames.getPath('number'),
        title: <Trans i18nKey="App.Transactions.Columns.Number" />,
        render: (value: string) => {
            return <DataView value={value} />;
        },
    },
    {
        key: 'merchantErpCode',
        dataIndex: formNames.getPath('merchantErpCode'),
        title: <Trans i18nKey="App.Transactions.Columns.MerchantErpCode" />,
        render: (value: string) => {
            return <DataView value={value} />;
        },
    },
    {
        key: 'outstandingAmountBeforeDeduction',
        dataIndex: formNames.getPath('outstandingAmountBeforeDeduction'),
        title: (
            <Trans i18nKey="App.Transactions.Columns.OutstandingAmountBeforeDeduction" />
        ),
        render: (value: string) => {
            return <DataView value={value} formatValue="currency-sar" />;
        },
    },
    {
        key: 'deductedAmount',
        dataIndex: formNames.getPath('deductedAmount'),
        title: <Trans i18nKey="App.Transactions.Columns.DeductedAmount" />,
        render: (value: string) => {
            return <DataView value={value} formatValue="currency-sar" />;
        },
    },
    {
        key: 'actualRoundAmount',
        dataIndex: formNames.getPath('actualRoundAmount'),
        title: <Trans i18nKey="App.Transactions.Columns.ActualRoundAmount" />,
        render: (value: string) => {
            return <DataView value={value} formatValue="currency-sar" />;
        },
    },
    {
        key: 'deductedPct',
        dataIndex: formNames.getPath('deductedPct'),
        title: <Trans i18nKey="App.Transactions.Columns.DeductedPct" />,
        render: (value: string) => {
            return <DataView value={value} formatValue="percent" />;
        },
    },
    {
        key: 'collectionCommissionAmount',
        dataIndex: formNames.getPath('collectionCommissionAmount'),
        title: (
            <Trans i18nKey="App.Transactions.Columns.CollectionCommissionAmount" />
        ),
        render: (value: string) => {
            return <DataView value={value} formatValue="currency-sar" />;
        },
    },
    {
        key: 'outstandingAmountAfterDeduction',
        dataIndex: formNames.getPath('outstandingAmountAfterDeduction'),
        title: (
            <Trans i18nKey="App.Transactions.Columns.OutstandingAmountAfterDeduction" />
        ),
        render: (value: string) => {
            return <DataView value={value} formatValue="currency-sar" />;
        },
    },
    {
        key: 'movedToTheNextCollectionCycleAmount',
        dataIndex: formNames.getPath('movedToTheNextCollectionCycleAmount'),
        title: (
            <Trans i18nKey="App.Transactions.Columns.MovedToTheNextCollectionCycleAmount" />
        ),
        render: (value: string) => {
            return <DataView value={value} formatValue="currency-sar" />;
        },
    },
    {
        key: 'status',
        dataIndex: formNames.getPath('status'),
        title: <Trans i18nKey="App.Transactions.Columns.Status" />,
        render: (value: string) => {
            return (
                <DataView
                    value={value}
                    formatValue={(value) => (
                        <LoanApplicationStatusView status={value} />
                    )}
                />
            );
        },
    },
    {
        key: 'invoiceNumber',
        dataIndex: formNames.getPath('invoiceNumber'),
        title: <Trans i18nKey="App.Transactions.Columns.InvoiceNumber" />,
        render: (value: string) => {
            return <DataView value={value} />;
        },
    },
    {
        key: 'grossMerchandiseVolumeAmount',
        dataIndex: formNames.getPath('grossMerchandiseVolumeAmount'),
        title: (
            <Trans i18nKey="App.Transactions.Columns.GrossMerchandiseVolumeAmount" />
        ),
        render: (value: string) => {
            return <DataView value={value} formatValue="currency-sar" />;
        },
    },
    {
        key: 'comment',
        dataIndex: formNames.getPath('comment'),
        title: <Trans i18nKey="App.Transactions.Columns.Comment" />,
        render: (value: string) => {
            return <DataView value={value} />;
        },
    },
    {
        key: 'company',
        dataIndex: formNames.getPath('company'),
        title: <Trans i18nKey="App.Transactions.Columns.Company" />,
        render: (value: string) => {
            return <DataView value={value} />;
        },
    },
];

const StyledTable = styled(Table)`
    .ant-table-content {
        overflow-x: auto;
    }

    .ant-table-thead > tr > th {
        white-space: nowrap;
    }

    .ant-table-tbody > tr > td {
        white-space: nowrap;
    }

    .ant-pagination-options {
        width: auto !important;
    }
`;
